import React, { Component } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/withStyles';
import { Table, Column, Cell } from 'fixed-data-table-2';
import { Row, Col, Button, Tabs, Tab } from 'react-bootstrap';

import { BarChart, Bar, XAxis, YAxis, Legend } from 'recharts';
import d3 from '../../core/d3_3.5.17';
import s from './MultiNetworkEdge.css';

import PredictionCell from '../PredictionCell';
import Filter from '../Filter';

/*
 * Component for displaying an edge distribution
 */

class MultiNetworkEdge extends Component {
  static propTypes = {
    // Title to display
    title: PropTypes.object,

    // Array of edges from different networks
    edgeNetworks: PropTypes.array.isRequired,

    // The selcted edge to highlight
    edge: PropTypes.object.isRequired,

    // Network label
    networkTitle: PropTypes.string,

    // Width of parent container
    containerWidth: PropTypes.number,
  };

  constructor(props) {
    super(props);
    this.updateData = this.updateData.bind(this);
  }

  static defaultProps = {
    networkTitle: 'Selected',
    containerWidth: 540,
  };

  updateData() {
    const values = this.props.edgeNetworks.map(edge => edge.posterior);
    const hist = d3.layout
      .histogram()
      .range([0, 1])
      .bins(10)(values);

    // The bar corresponding to the given edge weight will be a separate
    // label, with a different color
    this._data = hist.map((bin, idx) => ({
      name: bin.x.toFixed(1).toString(),
      All:
        bin.x < this.props.edge.weight && bin.x + 0.1 > this.props.edge.weight
          ? 0
          : bin.y,
      [this.props.networkTitle]:
        bin.x < this.props.edge.weight && bin.x + 0.1 > this.props.edge.weight
          ? bin.y
          : 0,
    }));
  }

  shouldComponentUpdate(nextProps, nextState) {
    return (
      nextProps.edgeNetworks !== this.props.edgeNetworks ||
      nextProps.edge != this.props.edge ||
      nextProps.containerWidth != this.props.containerWidth
    );
  }

  render() {
    this.updateData();
    return (
      <div className={s.root}>
        {this.props.title}
        <BarChart
          data={this._data}
          width={this.props.containerWidth}
          height={220}
          margin={{ top: 10, bottom: 20, left: 10, right: 10 }}
        >
          <Bar
            animationBegin={0}
            animationDuration={500}
            dataKey="All"
            stackId="a"
            fill="#337ab7"
          />
          <Bar
            animationBegin={0}
            animationDuration={500}
            dataKey={this.props.networkTitle}
            stackId="a"
            fill="#82ca9d"
          />
          <XAxis
            dataKey="name"
            label={{
              value: 'edge weight',
              position: 'insideBottomRight',
              dy: 10,
            }}
          />
          <YAxis
            label={{
              value: '# of networks',
              position: 'insideLeft',
              angle: -90,
              dy: 15,
            }}
          />
          <Legend height={30} />
        </BarChart>
        <FilteredNetworkTable
          networks={this.props.edgeNetworks}
          width={this.props.containerWidth}
          containerWidth={this.props.containerWidth}
          getField={(network, field) => network.context[field]}
          saveFields={['title', 'posterior']}
          saveHeaders={['NETWORK', 'SCORE']}
          saveFile="multi_network"
        />
      </div>
    );
  }
}

// Cell for context object
const ContextCell = ({ rowIndex, data, col, ...props }) => (
  <Cell {...props}>{data[rowIndex].context[col]}</Cell>
);

// Cell for term object
const TermCell = ({ rowIndex, data, col, ...props }) => (
  <Cell {...props}>
    {data[rowIndex].context.term && data[rowIndex].context.term[col]}
  </Cell>
);

const NetworkTable = ({ networks, containerWidth }) => {
  return (
    <Table
      rowHeight={40}
      rowsCount={networks.length}
      width={containerWidth}
      height={40 * 10 + 30}
      headerHeight={30}
    >
      <Column
        header={<Cell>NETWORK</Cell>}
        width={120}
        cell={<ContextCell data={networks} col="title" />}
      />
      <Column
        header={<Cell>DESCRIPTION</Cell>}
        width={200}
        flexGrow={1}
        cell={<TermCell data={networks} col="description" />}
      />
      <Column
        header={<Cell>CONFIDENCE</Cell>}
        width={100}
        cell={<PredictionCell data={networks} col="posterior" />}
      />
    </Table>
  );
};

const FilteredNetworkTable = Filter({
  dataField: 'networks',
  fields: ['title', 'description'],
})(NetworkTable);

export default withStyles(s)(MultiNetworkEdge);
